/**
 * OAuth 2.1 consent screen.
 *
 * Reached by /oauth/authorize redirecting the browser here with all the
 * standard OAuth params preserved as query string. Responsibilities:
 *
 *   1. Verify the user is logged in (redirect to /login with a return_to
 *      that brings them back here, params intact).
 *   2. Fetch /api/oauth/authorize-info to validate the client_id and
 *      retrieve the client display name to show on the consent screen.
 *   3. On Approve / Deny: POST /api/oauth/decision and follow the
 *      redirect_url it returns (which sends us back to the OAuth client
 *      with either ?code=... or ?error=access_denied).
 *
 * We deliberately do NOT use the Sidebar layout here — this screen is the
 * OAuth client's gateway into Storm Pad, so it should look like a focused
 * permission prompt, not the full app shell.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldCheck, AlertTriangle, Target } from "lucide-react";
import { toast } from "sonner";
import { formatErrorDetail } from "@/lib/format-error";

const OAuthConsent = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null); // { client_id, client_name, scope }
  const [error, setError] = useState(null);
  // Only managers may connect apps (the backend enforces it); a sales rep
  // lands on a "Managers only" card instead of the consent form.
  const [forbidden, setForbidden] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allowSubscriptionCreation, setAllowSubscriptionCreation] = useState(false);
  const [allowSubscriptionEditing, setAllowSubscriptionEditing] = useState(false);
  const token = localStorage.getItem("token");

  // Capture all the OAuth params so we can echo them back to /api/oauth/decision.
  const oauthParams = useMemo(() => ({
    client_id: params.get("client_id") || "",
    redirect_uri: params.get("redirect_uri") || "",
    scope: params.get("scope") || "mcp",
    state: params.get("state") || "",
    code_challenge: params.get("code_challenge") || "",
    code_challenge_method: params.get("code_challenge_method") || "",
  }), [params]);

  useEffect(() => {
    // Sanity-check the params before bothering the server.
    if (!oauthParams.client_id || !oauthParams.redirect_uri || !oauthParams.code_challenge) {
      setError("Missing required OAuth parameters. The authorization request is malformed.");
      setLoading(false);
      return;
    }

    setAllowSubscriptionCreation(false);
    setAllowSubscriptionEditing(false);
    const fetchInfo = async () => {
      try {
        const r = await axios.get(`${API}/oauth/authorize-info`, {
          params: { client_id: oauthParams.client_id, redirect_uri: oauthParams.redirect_uri },
          headers: { Authorization: `Bearer ${token}` },
        });
        setInfo(r.data);
      } catch (err) {
        if (err.response?.status === 401) {
          // Not logged in — bounce through /login and come back here intact.
          const returnTo = `${window.location.pathname}${window.location.search}`;
          navigate(`/login?return_to=${encodeURIComponent(returnTo)}`);
          return;
        }
        setForbidden(err.response?.status === 403);
        setError(formatErrorDetail(err, "Failed to validate authorization request."));
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [oauthParams, token, navigate]);

  const submitDecision = async (approve) => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/oauth/decision`,
        { ...oauthParams, approve, allow_subscription_creation: allowSubscriptionCreation, allow_subscription_editing: allowSubscriptionEditing },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Hand control back to the OAuth client. Use replace so the consent
      // page doesn't sit in browser history with the (now spent) auth code.
      window.location.replace(r.data.redirect_url);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to record your decision"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="oauth-consent-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Storm Pad
          </span>
        </div>

        {loading && (
          <Card>
            <CardContent className="py-12 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </CardContent>
          </Card>
        )}

        {!loading && error && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                {forbidden ? "Managers only" : "Authorization request invalid"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600" data-testid="oauth-error-message">{error}</p>
              {forbidden && (
                // Declining tells the app access was denied, instead of leaving it waiting.
                <Button
                  className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={submitting}
                  onClick={() => submitDecision(false)}
                  data-testid="oauth-forbidden-cancel-btn"
                >
                  {submitting ? "Working…" : "Cancel and return to the app"}
                </Button>
              )}
              <Button
                variant="outline"
                className={forbidden ? "mt-2 w-full" : "mt-4 w-full"}
                onClick={() => navigate("/dashboard")}
              >
                Back to Storm Pad
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && info && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                Authorize {info.client_name}
              </CardTitle>
              <CardDescription>
                {info.client_name} is requesting access to your Storm Pad organization
                {user?.org_name ? ` (${user.org_name})` : ""}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  This will let {info.client_name}:
                </p>
                <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
                  <li>Search your subscriptions and leads (read-only)</li>
                  <li>View your sales dashboard metrics</li>
                  <li>List subscription renewals due soon</li>
                </ul>
                <label className="flex items-start gap-2 text-sm text-slate-700 pt-2">
                  <input type="checkbox" checked={allowSubscriptionCreation}
                    onChange={e => setAllowSubscriptionCreation(e.target.checked)}
                    data-testid="oauth-allow-subscription-creation" />
                  <span>Also allow creating customer subscriptions, including prices, payment status,
                    signup notes, general notes and renewals. This does not allow editing or deleting existing subscriptions.</span>
                </label>
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={allowSubscriptionEditing}
                  onChange={e => setAllowSubscriptionEditing(e.target.checked)}
                  data-testid="oauth-allow-subscription-editing" />
                <span>Allow editing existing customer subscriptions, including payment status, notes and renewals.</span>
              </label>
              </div>

              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle>Only approve clients you trust</AlertTitle>
                <AlertDescription>
                  Approving grants {info.client_name} ongoing read access
                  {allowSubscriptionCreation ? " and permission to create subscriptions" : ""}
                  {allowSubscriptionEditing ? " and permission to edit subscriptions" : ""}.
                  Only enable these permissions for an app you trust to add or change customer records.
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={submitting}
                  onClick={() => submitDecision(false)}
                  data-testid="oauth-deny-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  disabled={submitting}
                  onClick={() => submitDecision(true)}
                  data-testid="oauth-approve-btn"
                >
                  {submitting ? "Working…" : "Approve"}
                </Button>
              </div>

              <p className="text-xs text-slate-400 text-center">
                Signed in as {user?.email}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OAuthConsent;
